import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GlassSpinner from "../../components/GlassSpinner";
import { useEmpresa } from "../../context/EmpresaContext";
import { getEntradas, getEventos, getRRPP } from "../../services/sininApi";

function Bar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span><b>{value} ({pct}%)</b>
      </div>
      <div style={{ background: "#e9ecef", borderRadius: 10, height: 10 }}>
        <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 10, transition: "width .4s" }} />
      </div>
    </div>
  );
}

export default function ClienteEstadisticas() {
  const { idEvento } = useParams();
  const navigate = useNavigate();
  const { empresa, colores } = useEmpresa();
  const [evento, setEvento] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!empresa?.empresaId) return;
    setLoading(true);
    setError("");
    try {
      const [eventos, entradasRes, rrppRes] = await Promise.all([
        getEventos(empresa.empresaId, empresa.loginType || "1"),
        getEntradas(empresa.empresaId, { idEvento }),
        getRRPP(empresa.empresaId),
      ]);
      const eventoActual = (Array.isArray(eventos) ? eventos : []).find(ev => String(ev.idEvento) === String(idEvento));
      const entradas = Array.isArray(entradasRes?.items) ? entradasRes.items : [];
      const rrpp = Array.isArray(rrppRes?.items) ? rrppRes.items : [];
      setEvento(eventoActual || { name: idEvento, tandas: [] });
      setStats(computeStats(entradas, eventoActual?.tandas || [], rrpp));
    } catch {
      setError("No pudimos cargar las estadísticas del evento.");
    } finally {
      setLoading(false);
    }
  }, [empresa, idEvento]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ ...styles.page, background: colores.fondo }}>
      <GlassSpinner size={80} background={colores.fondo} />
    </div>
  );

  return (
    <div style={{ ...styles.shell, background: `linear-gradient(180deg, ${colores.primario}22, #f4f4f4 260px)` }}>
      <header style={{ ...styles.header, background: colores.fondo, color: colores.fuente }}>
        <button style={{ ...styles.back, color: colores.fuente }} onClick={() => navigate(`/cliente/invitados/${idEvento}`)}>← INVITADOS</button>
        <div style={{ textAlign: "center" }}>
          {empresa?.logoUrl && <img src={empresa.logoUrl} alt="" style={{ width: 42, height: 42, objectFit: "contain", display: "block", margin: "0 auto 4px" }} />}
          <b>{evento?.name || evento?.nombre || idEvento}</b>
          <div style={{ fontSize: 10, opacity: .72 }}>ESTADÍSTICAS</div>
        </div>
        <button style={{ ...styles.refresh, color: colores.fuente }} onClick={load}>↻</button>
      </header>

      <main style={styles.wrap}>
        {error ? <div style={styles.error}>{error}</div> : stats && (
          <>
            <div style={styles.cards}>
              {[
                ["TOTAL", stats.total, "#202428", "#fff"],
                ["INGRESARON", stats.ingresaron, "#d1e7dd", "#0a5234"],
                ["PENDIENTES", stats.pendientes, "#cfe2ff", "#084298"],
                ["CORTESÍAS", stats.cortesias, "#f8d7da", "#842029"],
                ["PAGOS", stats.pagos, "#fff3cd", "#664d03"],
              ].map(([label, value, bg, color]) => (
                <div key={label} style={{ ...styles.statCard, background: bg, color }}>
                  <strong style={styles.statValue}>{value}</strong><span>{label}</span>
                </div>
              ))}
            </div>

            <StatsSection title="POR TIPO DE TICKET">
              {stats.porTanda.length ? stats.porTanda.map(item => (
                <Bar key={item.label} label={item.label} value={item.count} max={stats.total} color={colores.primario} />
              )) : <span>Sin tipos registrados.</span>}
            </StatsSection>

            <StatsSection title="POR VENDEDOR">
              {stats.porVendedor.length ? stats.porVendedor.map(item => (
                <Bar key={item.id} label={item.nombre} value={item.count} max={stats.total} color="#1976d2" />
              )) : <span>Sin vendedores registrados.</span>}
            </StatsSection>

            <StatsSection title="POR SEXO">
              {stats.porSexo.map(item => (
                <Bar key={item.label} label={item.label} value={item.count} max={stats.total} color={item.color} />
              ))}
            </StatsSection>

            {stats.porDia.length > 0 && (
              <StatsSection title="TICKETS CREADOS POR DÍA">
                {stats.porDia.map(item => (
                  <Bar key={item.label} label={item.label} value={item.count}
                    max={Math.max(...stats.porDia.map(x => x.count))} color={colores.primario} />
                ))}
              </StatsSection>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function StatsSection({ title, children }) {
  return <section style={styles.section}><h3 style={styles.title}>{title}</h3>{children}</section>;
}

function computeStats(entradas, tandas, rrpp) {
  const rrppNames = Object.fromEntries(rrpp.map(item => [String(item.dni), `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.dni]));
  const countBy = keyFn => entradas.reduce((acc, item) => {
    const key = String(keyFn(item) || "Sin dato");
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const tandaCounts = countBy(item => item.tanda || item.tandaId || "Sin tipo");
  const vendedorCounts = countBy(item => item.createdBy || "Sin vendedor");
  const sexoCounts = countBy(item => item.sexo || item.gender || "Sin dato");
  const diaCounts = countBy(item => String(item.fechaIngreso || item.createdAt || "").slice(0, 10) || "Sin fecha");
  const tandaLabels = Array.from(new Set([...(tandas || []).map(t => t.label).filter(Boolean), ...Object.keys(tandaCounts)]));

  return {
    total: entradas.length,
    ingresaron: entradas.filter(item => Boolean(item.used)).length,
    pendientes: entradas.filter(item => !item.used).length,
    cortesias: entradas.filter(item => String(item.tipoPago).toUpperCase() === "CORTESIA").length,
    pagos: entradas.filter(item => String(item.tipoPago).toUpperCase() === "PAGO").length,
    porTanda: tandaLabels.map(label => ({ label, count: tandaCounts[label] || 0 })).filter(item => item.count > 0),
    porVendedor: Object.entries(vendedorCounts).map(([id, count]) => ({ id, nombre: rrppNames[id] || (id === "online" ? "Venta online" : id), count })).sort((a, b) => b.count - a.count),
    porSexo: [
      { label: "Femenino", count: sexoCounts.Femenino || sexoCounts.F || 0, color: "#e91e8c" },
      { label: "Masculino", count: sexoCounts.Masculino || sexoCounts.M || 0, color: "#1976d2" },
      { label: "Otro / sin dato", count: entradas.length - (sexoCounts.Femenino || sexoCounts.F || 0) - (sexoCounts.Masculino || sexoCounts.M || 0), color: "#777" },
    ],
    porDia: Object.entries(diaCounts).filter(([label]) => label !== "Sin fecha").sort(([a], [b]) => a.localeCompare(b)).map(([label, count]) => ({ label, count })),
  };
}

const styles = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" },
  shell: { minHeight: "100vh" },
  header: { padding: "13px 16px", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 },
  back: { justifySelf: "start", background: "transparent", border: 0, cursor: "pointer", fontWeight: 800, fontSize: 11 },
  refresh: { justifySelf: "end", background: "transparent", border: 0, cursor: "pointer", fontWeight: 900, fontSize: 22 },
  wrap: { padding: 16, maxWidth: 720, margin: "0 auto" },
  cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, marginBottom: 16 },
  statCard: { borderRadius: 16, padding: "15px 12px", textAlign: "center", display: "flex", flexDirection: "column", gap: 3, boxShadow: "0 3px 10px rgba(0,0,0,.1)", fontSize: 11, fontWeight: 800 },
  statValue: { fontSize: 30, lineHeight: 1 },
  section: { background: "#fff", borderRadius: 16, padding: "16px 18px", marginBottom: 14, boxShadow: "0 3px 12px rgba(0,0,0,.08)", color: "#333", fontSize: 13 },
  title: { margin: "0 0 14px", fontSize: 13, fontWeight: 900, color: "#1a1a1a" },
  error: { background: "#f8d7da", color: "#842029", borderRadius: 16, padding: 16, textAlign: "center", fontWeight: 700 },
};
